import React from 'react';
import {
  Icon,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Grid,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  CircularProgress,
  DialogActions,
  Checkbox,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { CompareSearchService, ReportService } from '../../hooks';
import { useFarmDateTime } from '../../hooks/useFarmDateTime';
import { DayOfWeek, SearchCompareViewModel } from '../../models/common';
import { AnimalTrendDataType, AnimalTrendDataTypeTranslations } from '../../models/animal';
import {
  ReportFrequency,
  ReportFrequencyTranslations,
  ReportSourceViewModel,
  ScheduledReportViewModel
} from '../../models/report';
import { RelativeDateWindow, RelativeDateWindowTranslations } from '../../utils/date-range';
import DayOfWeekSelect from '../../elements/DayOfWeekSelect';
import TrendDataMulti from '../TrendDataMulti/component';
import { AnimalTrendData, toAnimalTrendDataByKey } from '../../models/animal-trend-data';
import { extractApiErrorMessage, notifications, summarizeSearchQueryFilters } from '../../utils';

interface IProps {
  farmId: string;
  /** When set, the modal edits this report instead of creating a new one. */
  report?: ScheduledReportViewModel;
  /** Hides the internal "Schedule Report" trigger button - used when editing, where the dialog is opened externally. */
  hideTrigger?: boolean;
  /** Called when the dialog closes, whether cancelled or saved. Required to unmount an edit instance. */
  onClose?: () => void;
  onSaved?: () => void;
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const ScheduleReportModal = ({ farmId, report, hideTrigger, onClose, onSaved }: IProps) => {
  const { t } = useTranslation();
  const { formatDate } = useFarmDateTime();
  const isEditing = !!report;

  const compareSearchService = new CompareSearchService();
  const reportService = new ReportService();

  const [dialogOpen, setDialogOpen] = React.useState(isEditing);
  const [isBusy, setIsBusy] = React.useState(false);
  const [isSaving, setIsSaving] = React.useState(false);
  const [isPreviewing, setIsPreviewing] = React.useState(false);

  const [savedSearchQueries, setSavedSearchQueries] = React.useState<SearchCompareViewModel[]>([]);
  const [selectedIds, setSelectedIds] = React.useState<string[]>(
    report ? report.sources.map((s) => s.savedSearchId) : []
  );

  const [reportName, setReportName] = React.useState(report?.name ?? '');
  const [animalTrendDataType, setAnimalTrendDataType] = React.useState<AnimalTrendDataType>(
    report?.animalTrendDataType ?? AnimalTrendDataType.SlaughterhouseDataWeight
  );
  const [relativeDateWindow, setRelativeDateWindow] = React.useState<RelativeDateWindow>(
    report?.relativeDateWindow ?? RelativeDateWindow.Last7Days
  );
  const [frequency, setFrequency] = React.useState<ReportFrequency>(report?.frequency ?? ReportFrequency.Weekly);
  const [dayOfWeek, setDayOfWeek] = React.useState<DayOfWeek>(report?.dayOfWeek ?? DayOfWeek.Monday);
  const [recipientsText, setRecipientsText] = React.useState(report?.recipients.join(', ') ?? '');

  const [previewData, setPreviewData] = React.useState<Record<string, AnimalTrendData>>();
  const [previewSources, setPreviewSources] = React.useState<ReportSourceViewModel[]>([]);

  React.useEffect(() => {
    if (isEditing) {
      loadSearchQueries();
    }
  }, []);

  const openDialog = () => {
    setDialogOpen(true);
    loadSearchQueries();
  };

  const resetForm = () => {
    setSelectedIds(report ? report.sources.map((s) => s.savedSearchId) : []);
    setReportName(report?.name ?? '');
    setAnimalTrendDataType(report?.animalTrendDataType ?? AnimalTrendDataType.SlaughterhouseDataWeight);
    setRelativeDateWindow(report?.relativeDateWindow ?? RelativeDateWindow.Last7Days);
    setFrequency(report?.frequency ?? ReportFrequency.Weekly);
    setDayOfWeek(report?.dayOfWeek ?? DayOfWeek.Monday);
    setRecipientsText(report?.recipients.join(', ') ?? '');
    setPreviewData(undefined);
    setPreviewSources([]);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    resetForm();
    onClose?.();
  };

  const loadSearchQueries = () => {
    setIsBusy(true);

    compareSearchService
      .getAllAsync(farmId)
      .then((result) => {
        setSavedSearchQueries(result);
        setIsBusy(false);
      })
      .catch((e) => {
        setIsBusy(false);
        notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
      });
  };

  const handleCheckboxChange = (id: string) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]));
    setPreviewData(undefined);
  };

  const buildSelectedSources = (): ReportSourceViewModel[] =>
    savedSearchQueries
      .filter((item) => selectedIds.includes(item.id.toString()))
      .map((item) => ({ savedSearchId: item.id.toString(), name: item.name, query: item.query }));

  const parseRecipients = (): string[] =>
    recipientsText
      .split(',')
      .map((email) => email.trim())
      .filter((email) => email.length > 0);

  const validate = (): string | null => {
    if (selectedIds.length === 0) {
      return t('f-report-validation-no-source');
    }
    const recipients = parseRecipients();
    if (recipients.length === 0) {
      return t('f-report-validation-no-recipients');
    }
    if (recipients.some((email) => !EMAIL_REGEX.test(email))) {
      return t('f-report-validation-invalid-email');
    }
    return null;
  };

  const handlePreview = async () => {
    if (selectedIds.length === 0) {
      notifications.warning(t('f-report-validation-no-source'));
      return;
    }

    setIsPreviewing(true);
    try {
      const sources = buildSelectedSources();
      const data = await reportService.previewAsync(animalTrendDataType, relativeDateWindow, sources);
      setPreviewSources(sources);
      setPreviewData(toAnimalTrendDataByKey(data));
    } catch (e) {
      notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
    } finally {
      setIsPreviewing(false);
    }
  };

  const handleSave = async () => {
    const validationError = validate();
    if (validationError) {
      notifications.warning(validationError);
      return;
    }

    setIsSaving(true);
    try {
      const input = {
        name: reportName,
        animalTrendDataType,
        relativeDateWindow,
        frequency,
        dayOfWeek,
        recipients: parseRecipients(),
        sourceIds: selectedIds
      };

      if (report) {
        await reportService.updateAsync(report.id, input, buildSelectedSources());
        notifications.success(t('f-report-update-ok'));
      } else {
        await reportService.createAsync(input, buildSelectedSources());
        notifications.success(t('f-report-save-ok'));
      }
      closeDialog();
      onSaved?.();
    } catch (e) {
      notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
    } finally {
      setIsSaving(false);
    }
  };

  const selectedSourceNames = savedSearchQueries
    .filter((item) => selectedIds.includes(item.id.toString()))
    .map((item) => item.name);

  return (
    <>
      {!hideTrigger && (
        <Button
          id="scheduleReportBtn"
          variant="outlined"
          color="default"
          className="!m-1"
          startIcon={<Icon>{'schedule_send'}</Icon>}
          onClick={openDialog}
        >
          {t('f-report-schedule-report')}
        </Button>
      )}

      <Dialog id="scheduleReportDialog" open={dialogOpen} onClose={closeDialog} maxWidth="md" fullWidth>
        <DialogTitle className="!bg-gray-50">
          {t(isEditing ? 'f-report-edit-title' : 'f-report-schedule-report-title')}
        </DialogTitle>

        <DialogContent>
          {isBusy ? (
            <div className="flex justify-center py-12">
              <CircularProgress size={72} />
            </div>
          ) : (
            <div className="flex flex-col gap-6 py-2">
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-2">{t('f-report-select-sources')}</h4>
                {savedSearchQueries.length === 0 ? (
                  <p className="text-sm text-gray-500">{t('f-report-no-saved-searches')}</p>
                ) : (
                  <div className="border border-gray-200 rounded-lg overflow-hidden max-h-72 overflow-y-auto">
                    <Table size="small">
                      <TableBody>
                        {savedSearchQueries.map((item) => {
                          const filterLabels = summarizeSearchQueryFilters(item.query, t, formatDate);
                          return (
                            <TableRow key={item.id} hover>
                              <TableCell padding="checkbox">
                                <Checkbox
                                  checked={selectedIds.includes(item.id.toString())}
                                  onChange={() => handleCheckboxChange(item.id.toString())}
                                />
                              </TableCell>
                              <TableCell>
                                <div className="font-medium"># {item.name}</div>
                                {filterLabels.length > 0 ? (
                                  <div className="flex flex-wrap gap-1 mt-1">
                                    {filterLabels.map((label, index) => (
                                      <Chip
                                        key={index}
                                        size="small"
                                        variant="outlined"
                                        label={label}
                                        className="!h-5 !text-xs"
                                      />
                                    ))}
                                  </div>
                                ) : (
                                  <div className="text-xs text-gray-400 mt-1">{t('f-report-no-filters')}</div>
                                )}
                              </TableCell>
                              <TableCell className="whitespace-nowrap align-top">
                                {new Date(item.timestamp).toLocaleDateString()}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </div>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label={t('f-report-name')}
                    variant="outlined"
                    size="small"
                    fullWidth
                    value={reportName}
                    onChange={(e) => setReportName(e.target.value)}
                    placeholder={selectedSourceNames.join(' + ') || undefined}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormControl variant="outlined" size="small" fullWidth>
                    <InputLabel id="reportMetricLabel">{t('f-com-animal-trend-type')}</InputLabel>
                    <Select
                      label={t('f-com-animal-trend-type')}
                      labelId="reportMetricLabel"
                      value={animalTrendDataType}
                      onChange={(e) => {
                        setAnimalTrendDataType(e.target.value as AnimalTrendDataType);
                        setPreviewData(undefined);
                      }}
                    >
                      {Object.values(AnimalTrendDataType)
                        .filter((v) => typeof v === 'number')
                        .map((value) => (
                          <MenuItem key={value} value={value}>
                            {t(AnimalTrendDataTypeTranslations[value as AnimalTrendDataType])}
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormControl variant="outlined" size="small" fullWidth>
                    <InputLabel id="reportWindowLabel">{t('f-report-relative-window')}</InputLabel>
                    <Select
                      label={t('f-report-relative-window')}
                      labelId="reportWindowLabel"
                      value={relativeDateWindow}
                      onChange={(e) => {
                        setRelativeDateWindow(e.target.value as RelativeDateWindow);
                        setPreviewData(undefined);
                      }}
                    >
                      {Object.values(RelativeDateWindow).map((value) => (
                        <MenuItem key={value} value={value}>
                          {t(RelativeDateWindowTranslations[value])}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={frequency === ReportFrequency.Weekly ? 3 : 6}>
                  <FormControl variant="outlined" size="small" fullWidth>
                    <InputLabel id="reportFrequencyLabel">{t('f-report-frequency')}</InputLabel>
                    <Select
                      label={t('f-report-frequency')}
                      labelId="reportFrequencyLabel"
                      value={frequency}
                      onChange={(e) => setFrequency(e.target.value as ReportFrequency)}
                    >
                      {Object.values(ReportFrequency).map((value) => (
                        <MenuItem key={value} value={value}>
                          {t(ReportFrequencyTranslations[value])}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {frequency === ReportFrequency.Weekly && (
                  <Grid item xs={12} sm={3}>
                    <DayOfWeekSelect value={dayOfWeek} onChange={setDayOfWeek} fullWidth variant="outlined" />
                  </Grid>
                )}

                <Grid item xs={12}>
                  <TextField
                    label={t('f-report-recipients')}
                    helperText={t('f-report-recipients-helper')}
                    variant="outlined"
                    size="small"
                    fullWidth
                    value={recipientsText}
                    onChange={(e) => setRecipientsText(e.target.value)}
                    placeholder="name@example.com, other@example.com"
                  />
                </Grid>
              </Grid>

              <div>
                <Button
                  variant="outlined"
                  color="primary"
                  size="small"
                  startIcon={isPreviewing ? <CircularProgress size={16} /> : <Icon>{'visibility'}</Icon>}
                  onClick={handlePreview}
                  disabled={isPreviewing || selectedIds.length === 0}
                >
                  {t('f-report-preview')}
                </Button>
              </div>

              {previewData && (
                <div className="border border-gray-200 rounded-lg p-4">
                  <TrendDataMulti
                    animalTrendDataType={animalTrendDataType}
                    trendData={Object.entries(previewData).map(([key, value]) => ({
                      name: previewSources[parseInt(key, 10)]?.name ?? `Search ${key}`,
                      color: undefined,
                      data: value.trendData.map((trend) => ({
                        ...trend,
                        id: parseInt(key, 10),
                        trendDataType: animalTrendDataType,
                        color: undefined
                      }))
                    }))}
                    slaughterhouseDataAverage={null}
                    weightAverage={null}
                    onAnimalTrendDataTypeChanged={() => {}}
                    disableTrendTypeSelect
                  />
                </div>
              )}
            </div>
          )}
        </DialogContent>

        <DialogActions className="!bg-gray-50">
          <Button onClick={closeDialog} startIcon={<Icon>{'close'}</Icon>} variant="outlined" color="primary">
            {t('f-com-cancel')}
          </Button>
          <Button
            onClick={handleSave}
            startIcon={isSaving ? <CircularProgress size={16} /> : <Icon>{'schedule_send'}</Icon>}
            variant="contained"
            color="primary"
            disabled={isSaving || isBusy}
          >
            {t(isEditing ? 'f-report-save-changes' : 'f-report-save-schedule')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ScheduleReportModal;
